import { useState, useMemo } from "react";
import { getErrorMessage, post, useInfiniteScroll } from "../lib/api";
import { accountPositionClosePath } from "../lib/accountRuntime";
import InfiniteListFooter from "./InfiniteListFooter";
import PositionChartModal from "./PositionChartModal";
import {
  Maximize2,
  Minimize2,
  Search,
  TrendingUp,
  TrendingDown,
  ShieldAlert,
  Zap,
  Sparkles,
  BarChart3,
} from "lucide-react";

const fmt = (v, digits = 4) => {
  if (typeof v !== "number" || isNaN(v)) return "—";
  if (Math.abs(v) < 0.0001 && v !== 0) return v.toExponential(2);
  if (Math.abs(v) >= 1000) return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v.toFixed(digits);
};

export default function PositionsTable({
  positions: rawPositions = [],
  listState = {},
  selectedViewAccountId = "paper",
  onAction,
  onOpenFullChart,
}) {
  const positions = useMemo(() => {
    if (Array.isArray(rawPositions)) return rawPositions;
    if (Array.isArray(rawPositions?.data)) return rawPositions.data;
    if (Array.isArray(rawPositions?.items)) return rawPositions.items;
    return [];
  }, [rawPositions]);
  const [activeChartPosition, setActiveChartPosition] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmCloseAll, setConfirmCloseAll] = useState(false);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [sideFilter, setSideFilter] = useState("all"); // all, long, short, profit, loss
  const [isExpanded, setIsExpanded] = useState(false);
  const isPaperAccount = selectedViewAccountId === "paper";
  const controlLabel = isPaperAccount ? "paper simulator" : "selected exchange account";

  const handleScroll = useInfiniteScroll(listState.loadMore || (() => {}), listState.hasMore);

  const positionActionError = (error, symbol) => {
    const detail = getErrorMessage(error);
    if (error?.response?.status === 404 || /^not found$/i.test(detail)) {
      return `${isPaperAccount ? "Paper" : "Exchange"} position ${symbol} is no longer open. Refreshing account positions.`;
    }
    return `Could not confirm the ${isPaperAccount ? "simulated" : "exchange"} close for ${symbol}: ${detail}`;
  };

  const close = async (id, symbol, side) => {
    if (confirmId !== id) {
      setConfirmId(id);
      setMessage(`Confirm close for ${symbol}`);
      return;
    }
    try {
      setMessage(`Closing ${symbol} in the ${controlLabel}...`);
      await post(accountPositionClosePath(selectedViewAccountId, symbol, side));
      setMessage(isPaperAccount
        ? `Paper position ${symbol} closed in simulation.`
        : `Exchange position ${symbol} close confirmed.`);
      if (onAction) onAction();
    } catch (e) {
      setMessage(positionActionError(e, symbol));
      if (e?.response?.status === 404 && onAction) onAction();
    } finally {
      setConfirmId(null);
    }
  };

  const closeAll = async () => {
    if (!confirmCloseAll) {
      setConfirmCloseAll(true);
      setMessage("⚠️ Click again to emergency CLOSE ALL active positions!");
      setTimeout(() => setConfirmCloseAll(false), 5000);
      return;
    }
    try {
      setMessage(`Closing all positions in the ${controlLabel}...`);
      let failed = 0;
      for (const p of positions) {
        try {
          await post(accountPositionClosePath(selectedViewAccountId, p.symbol, p.side));
        } catch {
          failed += 1;
        }
      }
      const closed = positions.length - failed;
      setMessage(failed
        ? `${closed} of ${positions.length} ${isPaperAccount ? "paper" : "exchange"} positions closed. Refresh before retrying the remaining ${failed}.`
        : isPaperAccount
          ? "All paper positions closed in simulation."
          : "All exchange position closes confirmed.");
      if (onAction) onAction();
    } catch (e) {
      setMessage(getErrorMessage(e));
    } finally {
      setConfirmCloseAll(false);
    }
  };

  // KPI Calculations
  const stats = useMemo(() => {
    let totalPnl = 0;
    let totalMargin = 0;
    let totalExposure = 0;
    let longs = 0;
    let shorts = 0;
    let winners = 0;
    let losers = 0;
    let bestWinner = null;
    let worstLoser = null;

    positions.forEach((p) => {
      const pnl = p.unrealized_pnl || 0;
      totalPnl += pnl;
      totalMargin += p.margin || 0;
      totalExposure += (p.qty || 0) * (p.mark_price || p.entry_price || 0);

      if (p.side === "long") longs++;
      else shorts++;

      if (pnl >= 0) {
        winners++;
        if (!bestWinner || pnl > bestWinner.unrealized_pnl) bestWinner = p;
      } else {
        losers++;
        if (!worstLoser || pnl < worstLoser.unrealized_pnl) worstLoser = p;
      }
    });

    return { totalPnl, totalMargin, totalExposure, longs, shorts, winners, losers, bestWinner, worstLoser };
  }, [positions]);

  // Filtered positions list
  const filteredPositions = useMemo(() => {
    return positions.filter((p) => {
      const sym = (p.symbol || "").toLowerCase();
      const matchSearch = !search || sym.includes(search.toLowerCase().trim());
      if (!matchSearch) return false;

      if (sideFilter === "long") return p.side === "long";
      if (sideFilter === "short") return p.side === "short";
      if (sideFilter === "profit") return (p.unrealized_pnl || 0) >= 0;
      if (sideFilter === "loss") return (p.unrealized_pnl || 0) < 0;
      return true;
    });
  }, [positions, search, sideFilter]);

  return (
    <div
      className={`bg-zinc-950 border border-zinc-800/80 rounded-xl shadow-2xl flex flex-col transition-all duration-300 ${
        isExpanded ? "fixed inset-2 z-50 bg-zinc-950/98 backdrop-blur-xl border-emerald-500/40 p-4" : "w-full min-h-0 flex-1"
      }`}
      data-testid="positions-panel"
    >
      {/* Top Header & Actions Bar */}
      <div className="px-4 py-3 border-b border-zinc-800/70 flex flex-wrap items-center justify-between gap-3 bg-zinc-900/40 rounded-t-xl">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
            <Zap size={16} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-100 font-mono">
                Open Positions ({positions?.length || 0})
              </h2>
              {stats.totalPnl >= 0 ? (
                <span className="px-2 py-0.5 text-[10px] font-mono font-semibold rounded bg-emerald-950/80 text-emerald-400 border border-emerald-800/60 flex items-center gap-1 shadow-sm">
                  <TrendingUp size={11} /> +${Number(stats.totalPnl || 0).toFixed(2)}
                </span>
              ) : (
                <span className="px-2 py-0.5 text-[10px] font-mono font-semibold rounded bg-rose-950/80 text-rose-400 border border-rose-800/60 flex items-center gap-1 shadow-sm">
                  <TrendingDown size={11} /> -${Math.abs(Number(stats.totalPnl || 0)).toFixed(2)}
                </span>
              )}
            </div>
            <p className="text-[10px] text-zinc-500 font-mono">
              {isPaperAccount ? "Simulated exposure & paper position controls" : "Exchange exposure & live position controls"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Emergency Close All Button */}
          {positions.length > 0 && (
            <button
              onClick={closeAll}
              className={`px-3 py-1.5 rounded text-[10px] font-mono font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 border shadow-sm ${
                confirmCloseAll
                  ? "bg-rose-600 text-white border-rose-400 animate-pulse shadow-rose-900/50"
                  : "bg-rose-950/40 text-rose-400 border-rose-900/80 hover:bg-rose-900/60 hover:border-rose-700"
              }`}
              title={isPaperAccount ? "Close all paper positions in simulation" : "Close all positions on the selected exchange account"}
            >
              <ShieldAlert size={13} />
              {confirmCloseAll ? "CONFIRM CLOSE ALL" : "Close All"}
            </button>
          )}

          {/* Fullscreen / Expand Toggle */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded-lg bg-zinc-900 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-zinc-800 transition-colors"
            title={isExpanded ? "Exit Fullscreen" : "Expand to Fullscreen"}
          >
            {isExpanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
        </div>
      </div>

      {/* KPI Cards Row */}
      {positions.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 py-2.5 bg-zinc-900/20 border-b border-zinc-800/60 text-[11px] font-mono">
          <div className="bg-zinc-900/50 border border-zinc-800/60 rounded-lg p-2 flex justify-between items-center">
            <span className="text-zinc-500">Long / Short</span>
            <span className="font-bold">
              <span className="text-emerald-400">{stats.longs} L</span> / <span className="text-rose-400">{stats.shorts} S</span>
            </span>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800/60 rounded-lg p-2 flex justify-between items-center">
            <span className="text-zinc-500">Winners / Losers</span>
            <span className="font-bold">
              <span className="text-emerald-400">{stats.winners} W</span> / <span className="text-rose-400">{stats.losers} L</span>
            </span>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800/60 rounded-lg p-2 flex justify-between items-center">
            <span className="text-zinc-500">Margin Used</span>
            <span className="font-bold text-zinc-200">${fmt(stats.totalMargin, 2)}</span>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800/60 rounded-lg p-2 flex justify-between items-center">
            <span className="text-zinc-500">Best Winner</span>
            <span className="font-bold text-emerald-400">
              {stats.bestWinner ? `${(stats.bestWinner.symbol || "").replace(":USDT", "")} (+$${Number(stats.bestWinner.unrealized_pnl || 0).toFixed(2)})` : "—"}
            </span>
          </div>
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="px-4 py-2 bg-zinc-900/30 border-b border-zinc-800/60 flex flex-wrap items-center justify-between gap-2">
        <div className="relative flex-1 min-w-[160px] max-w-[240px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search symbol..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md pl-8 pr-3 py-1 text-xs font-mono text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-emerald-500/60"
          />
        </div>

        <div className="flex items-center gap-1 text-[10px] font-mono">
          {[
            { id: "all", label: `All (${positions.length})` },
            { id: "long", label: `Long (${stats.longs})` },
            { id: "short", label: `Short (${stats.shorts})` },
            { id: "profit", label: `Profit (${stats.winners})` },
            { id: "loss", label: `Loss (${stats.losers})` },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSideFilter(tab.id)}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                sideFilter === tab.id
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold"
                  : "bg-zinc-900/60 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 border border-zinc-800/40"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div className="px-4 py-1.5 bg-amber-950/40 border-b border-amber-800/50 text-[11px] font-mono text-amber-300 flex items-center justify-between" data-testid="positions-message">
          <span>{message}</span>
          <button onClick={() => setMessage(null)} className="text-amber-500 hover:text-amber-300">✕</button>
        </div>
      )}

      {/* Main Positions Table Container */}
      <div className={`infinite-scroll-container flex-1 overflow-x-auto overflow-y-auto ${isExpanded ? "max-h-full" : "max-h-[500px]"}`} onScroll={handleScroll}>
        <table className="w-full text-xs font-mono border-collapse min-w-[850px]">
          {/* ISOLATED STICKY HEADER - ZERO OVERLAP GUARANTEE */}
          <thead className="sticky top-0 z-20 bg-zinc-950/98 backdrop-blur-md text-zinc-400 border-b border-zinc-800 shadow-md">
            <tr className="text-left text-[10px] uppercase tracking-wider">
              <th scope="col" className="py-2.5 px-3 font-semibold text-zinc-400 text-left bg-zinc-950">Symbol</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-center bg-zinc-950">Side</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Qty</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Entry</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Mark</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Margin</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">PnL ($)</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">PnL (%)</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-amber-400/90 text-right bg-zinc-950">Stop Loss</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-sky-400/90 text-right bg-zinc-950">Take Profit</th>
              <th scope="col" className="py-2.5 px-3 font-semibold text-zinc-400 text-center bg-zinc-950">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-zinc-900/80">
            {filteredPositions.length === 0 && (
              <tr>
                <td colSpan={11} className="py-12 text-center text-zinc-600" data-testid="no-positions">
                  <div className="flex flex-col items-center gap-2">
                    <Sparkles size={24} className="text-zinc-700 animate-pulse" />
                    <span>No matching open positions</span>
                    <span className="text-[10px] text-zinc-700">Scanner active & evaluating market opportunities</span>
                  </div>
                </td>
              </tr>
            )}

            {filteredPositions.map((p) => {
              const isProfit = (p.unrealized_pnl || 0) >= 0;
              const isLong = p.side === "long";
              const cleanSym = (p.symbol || "").replace(":USDT", "").replace("/USDT", "") || "UNKNOWN";

              return (
                <tr
                  key={p.id}
                  onClick={() => setActiveChartPosition(p)}
                  className="hover:bg-zinc-900/80 transition-colors group align-middle cursor-pointer"
                  data-testid={`position-row-${p.id}`}
                  title="Click to view live chart & levels"
                >
                  {/* Symbol Badge */}
                  <td className="py-2.5 px-3 font-bold text-zinc-100 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-6 rounded-full ${isLong ? "bg-emerald-500 shadow-emerald-500/50 shadow-sm" : "bg-rose-500 shadow-rose-500/50 shadow-sm"}`} />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span>{cleanSym}</span>
                          <span className="text-[9px] text-zinc-500 bg-zinc-900 px-1 py-0.2 rounded border border-zinc-800">USDT</span>
                          {p.leverage > 1 && (
                            <span className="text-[9px] font-bold text-amber-400/90 bg-amber-950/60 px-1.5 py-0.2 rounded border border-amber-800/60 font-mono">
                              {p.leverage}x
                            </span>
                          )}
                          <BarChart3 size={12} className="text-zinc-600 group-hover:text-emerald-400 transition-colors ml-0.5" />
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Side Pill */}
                  <td className="py-2.5 px-2 text-center whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        isLong
                          ? "bg-emerald-950/80 text-emerald-400 border border-emerald-800/60 shadow-sm shadow-emerald-950"
                          : "bg-rose-950/80 text-rose-400 border border-rose-800/60 shadow-sm shadow-rose-950"
                      }`}
                    >
                      {isLong ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                      {p.side}
                    </span>
                  </td>

                  {/* Qty */}
                  <td className="py-2.5 px-2 text-right text-zinc-300 font-mono whitespace-nowrap">{fmt(p.qty)}</td>

                  {/* Entry */}
                  <td className="py-2.5 px-2 text-right text-zinc-400 font-mono whitespace-nowrap">{fmt(p.entry_price)}</td>

                  {/* Mark Price */}
                  <td className="py-2.5 px-2 text-right text-zinc-200 font-semibold font-mono whitespace-nowrap">{fmt(p.mark_price)}</td>

                  {/* Margin */}
                  <td className="py-2.5 px-2 text-right text-zinc-400 font-mono whitespace-nowrap">
                    {p.margin ? `$${fmt(p.margin, 2)}` : "—"}
                  </td>

                  {/* PnL ($) */}
                  <td className={`py-2.5 px-2 text-right font-bold whitespace-nowrap ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>
                    <span className={`px-1.5 py-0.5 rounded ${isProfit ? "bg-emerald-950/40" : "bg-rose-950/40"}`}>
                      {isProfit ? "+" : ""}${fmt(p.unrealized_pnl, 2)}
                    </span>
                  </td>

                  {/* PnL (%) */}
                  <td className={`py-2.5 px-2 text-right font-bold whitespace-nowrap ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>
                    {isProfit ? "+" : ""}{(p.pnl_pct || 0).toFixed(2)}%
                  </td>

                  {/* SL */}
                  <td className="py-2.5 px-2 text-right font-mono text-amber-400/90 whitespace-nowrap">
                    {fmt(p.stop_loss)}
                  </td>

                  {/* TP */}
                  <td className="py-2.5 px-2 text-right font-mono text-sky-400/90 whitespace-nowrap">
                    {fmt(p.take_profit)}
                  </td>

                  {/* Close Action */}
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    <button
                      data-testid={`close-position-${cleanSym}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        close(p.id, p.symbol, p.side);
                      }}
                      className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase transition-all border ${
                        confirmId === p.id
                          ? "bg-rose-600 text-white border-rose-400 animate-pulse"
                          : "bg-rose-950/40 text-rose-400 border-rose-900/60 hover:bg-rose-900/80 hover:text-white"
                      }`}
                    >
                      {confirmId === p.id ? "Confirm" : "Close"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!!positions?.length && (
          <InfiniteListFooter
            loadingMore={listState.loadingMore}
            hasMore={listState.hasMore}
            error={listState.error}
          />
        )}
      </div>

      {activeChartPosition && (
        <PositionChartModal
          item={activeChartPosition}
          isOpen={Boolean(activeChartPosition)}
          onClose={() => setActiveChartPosition(null)}
          onOpenFullChart={onOpenFullChart}
        />
      )}
    </div>
  );
}
